import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
	Box,
	Button,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	FormControl,
	Select,
	MenuItem,
	InputLabel,
	Chip,
	Stack,
	Alert,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { AccessibleSector, AccessiblePlant, SectorData, SectorTypePlant, PlantPermission } from "models/dto/user";
import { UseTheme, UseMaterialColors } from "lib/hooks";
import { UseBusinessObjects } from "../hooks/useBusinessObjects";
import { GetPermissionColor, GetPermissionColorConfig } from "../permissionUtils";
import { GetPermissionSortOrder } from "lib/permissions/PermissionUtils";

interface sectorTypePlantPermissionTableProps {
	sector: AccessibleSector;
	plants: AccessiblePlant[];
	data: SectorData;
	onDataChange: (data: SectorData) => void;
	onRemove: () => void;
	isSectorAdmin: boolean;
	loading?: boolean;
}

interface plantRow {
	plantId: string;
	plantName: string;
	/** From GET plant-user; preserved for update/remove. */
	userRoleId?: string;
	permissions: PlantPermission[];
}

export function SectorTypePlantPermissionTable({
	sector,
	plants,
	data,
	onDataChange,
	onRemove,
	isSectorAdmin,
	loading = false,
}: sectorTypePlantPermissionTableProps) {
	const { theme } = UseTheme();
	const materialColors = UseMaterialColors();
	const {
		businessObjects,
		availableBusinessObjects,
		loading: businessObjectsLoading,
		error: businessObjectsError,
	} = UseBusinessObjects();
	const [plantRows, setPlantRows] = useState<plantRow[]>([]);

	// Use PermissionOrder from lib/permissions/PermissionUtils.ts for consistent sorting
	const sortPermissions = useCallback((permissions: PlantPermission[]): PlantPermission[] => {
		if (!businessObjects) {
			// If business objects not available, return unsorted (will show error in UI)
			return permissions;
		}
		return [...permissions].sort((a, b) => {
			const orderA = GetPermissionSortOrder(a.businessObjectId);
			const orderB = GetPermissionSortOrder(b.businessObjectId);
			return orderA - orderB;
		});
	}, [businessObjects]);

	// Initialize plant rows from data
	// Only update when we have plant names available to avoid showing IDs first
	useEffect(() => {
		// If no plants in data, clear rows
		if (data.plants.length === 0) {
			setPlantRows([]);
			return;
		}

		// Check if we have all the plant names we need
		const allPlantNamesAvailable = data.plants.every((plant) =>
			plants.some((p) => p.id === plant.plantId)
		);
		
		// Only update if we have all plant names available
		// This prevents showing IDs before names are loaded
		if (allPlantNamesAvailable && plants.length > 0) {
			const rows: plantRow[] = data.plants.map((plant) => {
				const plantInfo = plants.find((p) => p.id === plant.plantId);
				return {
					plantId: plant.plantId,
					plantName: plantInfo?.name || plant.plantId,
					userRoleId: plant.userRoleId,
					permissions: sortPermissions(plant.permissions || []),
				};
			});
			setPlantRows(rows);
		}
		// If plants are still loading but we have data, don't update yet
		// This prevents the flash of IDs
	}, [data.plants, plants, sortPermissions]);

	const updateSectorData = (rows: plantRow[]) => {
		const sectorData: SectorData = {
			sectorId: sector.id,
			plants: rows.map((row) => ({
				plantId: row.plantId,
				...(row.userRoleId !== undefined && { userRoleId: row.userRoleId }),
				permissions: sortPermissions(row.permissions),
			})),
		};
		onDataChange(sectorData);
	};

	const handleRemovePlant = (plantId: string) => {
		const newRows = plantRows.filter((row) => row.plantId !== plantId);
		setPlantRows(newRows);
		updateSectorData(newRows);
	};

	const handleAddPermission = (plantId: string, businessObjectId: string) => {
		const newRows = plantRows.map((row) => {
			if (row.plantId === plantId) {
				// Check if permission already exists
				if (
					row.permissions.some((p) => p.businessObjectId === businessObjectId)
				) {
					return row;
				}
				const newPermissions = [
					...row.permissions,
					{ businessObjectId },
				];
				return {
					...row,
					permissions: sortPermissions(newPermissions),
				};
			}
			return row;
		});
		setPlantRows(newRows);
		updateSectorData(newRows);
	};

	const handleRemovePermission = (
		plantId: string,
		businessObjectId: string
	) => {
		const newRows = plantRows.map((row) => {
			if (row.plantId === plantId) {
				const filteredPermissions = row.permissions.filter(
					(p) => p.businessObjectId !== businessObjectId
				);
				return {
					...row,
					permissions: sortPermissions(filteredPermissions),
				};
			}
			return row;
		});
		setPlantRows(newRows);
		updateSectorData(newRows);
	};

	const availablePlants = useMemo(() => {
		return plants.filter(
			(p) => !plantRows.some((row) => row.plantId === p.id)
		);
	}, [plants, plantRows]);

	return (
		<Paper
			sx={{
				mb: 2,
				overflow: "hidden",
			}}
		>
			{/* Sector Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					p: 1.5,
					backgroundColor: materialColors.primaryContainer,
					color: materialColors.onPrimaryContainer,
					position: "relative",
				}}
			>
				<Typography variant="h6" sx={{ color: materialColors.onPrimaryContainer }}>
					Sector: {sector.name}
				</Typography>
				<Button
					variant="outlined"
					color="error"
					onClick={onRemove}
					disabled={loading}
					startIcon={<DeleteOutlineIcon />}
					sx={{
						minWidth: 140,
						backgroundColor: "transparent",
						borderColor: "black",
						color: "black",
						"&:hover": {
							backgroundColor: "error.main",
							color: "white",
							borderColor: "error.main",
						},
					}}
				>
					Remove Sector
				</Button>
			</Box>

			<Box p={2}>
				{!businessObjectsLoading && businessObjectsError && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{businessObjectsError}
					</Alert>
				)}

				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Plant</TableCell>
								<TableCell>Permission</TableCell>
								<TableCell align="right">Action</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{plantRows.length === 0 ? (
								<TableRow>
									<TableCell colSpan={3} align="center" sx={{ py: 3 }}>
										<Typography variant="body2" color="text.secondary">
											No plant added
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								plantRows.map((row) => (
									<TableRow key={row.plantId}>
										<TableCell>{row.plantName}</TableCell>
										<TableCell>
											<Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
												{row.permissions.map((perm) => {
													const label =
														businessObjects?.find((bo) => bo.id === perm.businessObjectId)?.name ??
														perm.businessObjectId;
													const colorConfig = GetPermissionColorConfig(perm.businessObjectId);
													return (
														<Chip
															key={perm.businessObjectId}
															label={label}
															size="small"
															color={GetPermissionColor(perm.businessObjectId)}
															variant="filled"
															onDelete={() =>
																handleRemovePermission(
																	row.plantId,
																	perm.businessObjectId
																)
															}
															sx={{
																mr: 0.5,
																backgroundColor: colorConfig.backgroundColor,
																color: colorConfig.color,
																"& .MuiChip-DeleteOutlineIcon": {
																	color: colorConfig.color,
																},
															}}
														/>
													);
												})}
												<FormControl size="small" sx={{ minWidth: 40, ml: 0.5 }}>
													<Select
														value=""
														displayEmpty
														onChange={(e) => {
															const value = e.target.value;
															if (value) {
																handleAddPermission(row.plantId, value);
																// Reset select value
																setTimeout(() => {
																	const select = e.target as HTMLSelectElement;
																	if (select) select.value = "";
																}, 0);
															}
														}}
														disabled={loading || businessObjectsLoading}
														sx={{ 
															height: 28,
															"& .MuiSelect-select": { 
																padding: "4px 8px",
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
															},
														}}
														renderValue={() => <AddIcon fontSize="small" />}
													>
														<MenuItem value="" disabled>
															Select Permission
														</MenuItem>
														{availableBusinessObjects
															.filter(
																(bo) =>
																	!row.permissions.some(
																		(p) => p.businessObjectId === bo.id
																	)
															)
															.map((bo) => (
																<MenuItem key={bo.id} value={bo.id}>
																	{bo.label}
																</MenuItem>
															))}
													</Select>
												</FormControl>
											</Stack>
										</TableCell>
										<TableCell align="right">
											<IconButton
												size="small"
												onClick={() => handleRemovePlant(row.plantId)}
												color="error"
												disabled={loading}
											>
												<DeleteOutlineIcon />
											</IconButton>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
				
				{/* Add Plant button at bottom */}
				<Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2, mb: 1 }}>
						<FormControl sx={{ minWidth: "auto" }}>
							<Select
								value=""
								displayEmpty
								onChange={(e) => {
									const value = e.target.value;
									if (value) {
										const plant = availablePlants.find((p) => p.id === value);
										if (plant) {
											const newRow: plantRow = {
												plantId: plant.id,
												plantName: plant.name,
												permissions: [],
											};
											setPlantRows((prev) => [...prev, newRow]);
											updateSectorData([...plantRows, newRow]);
											// Reset select value
											setTimeout(() => {
												const select = e.target as HTMLSelectElement;
												if (select) select.value = "";
											}, 0);
										}
									}
								}}
								disabled={loading || availablePlants.length === 0}
								sx={{ 
									backgroundColor: "primary.main",
									color: "primary.contrastText",
									borderRadius: 1,
									boxShadow: "none",
									"& .MuiSelect-select": { 
										padding: theme.spacing(1, 2),
										paddingRight: theme.spacing(4),
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										textTransform: "none",
									},
									"& .MuiOutlinedInput-notchedOutline": {
										border: "none",
									},
									"&:hover:not(.Mui-disabled)": {
										backgroundColor: "primary.dark",
										boxShadow: "none",
									},
									"&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
										border: "none",
									},
									"&.Mui-focused": {
										backgroundColor: "primary.main",
										boxShadow: "none",
									},
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										border: "none",
									},
									"& .MuiSelect-icon": {
										color: "primary.contrastText",
										right: theme.spacing(1),
									},
									"&.Mui-disabled": {
										backgroundColor: "action.disabledBackground",
										color: "action.disabled",
										"& .MuiSelect-select": {
											color: "action.disabled",
										},
										"& .MuiSelect-icon": {
											color: "action.disabled",
										},
									},
								}}
								renderValue={() => (
									<Box display="flex" alignItems="center" gap={0.5}>
										<Typography 
											component="span" 
											sx={{ 
												color: availablePlants.length === 0 ? "action.disabled" : "white" 
											}}
										>
											+
										</Typography>
										<Typography 
											component="span" 
											sx={{ 
												color: availablePlants.length === 0 ? "action.disabled" : "white" 
											}}
										>
											Add Plant
										</Typography>
									</Box>
								)}
							>
								<MenuItem value="" disabled>
									Select Plant
								</MenuItem>
								{availablePlants.map((plant) => (
									<MenuItem key={plant.id} value={plant.id}>
										{plant.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
				</Box>
			</Box>
		</Paper>
	);
}

